import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Archive, ArchiveRestore } from 'lucide-react';
import { practitionerService } from '@/services/practitionerService';
import { toast } from '@/hooks/use-toast';
import { Practitioner } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { useClinic } from '@/contexts/ClinicContext';

interface ArchivePractitionerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practitioner: Practitioner | null;
  onSuccess: () => void;
}

const ArchivePractitionerDialog: React.FC<ArchivePractitionerDialogProps> = ({
  open,
  onOpenChange,
  practitioner,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { clinic } = useClinic();

  if (!practitioner) return null;

  // Check is_active field for archive/restore state (clinic-specific)
  const isArchived = !practitioner.is_active;
  const action = isArchived ? 'restore' : 'archive';
  const actionText = isArchived ? 'Restore' : 'Archive';
  const actionDescription = isArchived
    ? 'This will restore the practitioner for this clinic only. They will regain access to this clinic.'
    : 'This will archive the practitioner from this clinic only. They will still have access to other clinics they are linked to.';

  const handleConfirm = async () => {
    try {
      setIsLoading(true);

      if (isArchived) {
        await practitionerService.restorePractitioner(clinic?.id, practitioner.id);
        toast({
          title: 'Success',
          description: 'Practitioner has been restored for this clinic successfully.',
          variant: 'success',
        });
      } else {
        await practitionerService.archivePractitioner(clinic?.id, practitioner.id);
        toast({
          title: 'Success',
          description: 'Practitioner has been archived from this clinic successfully.',
          variant: 'success',
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error(`Error ${action}ing practitioner:`, error);
      toast({
        title: 'Error',
        description: error.message || `Failed to ${action} practitioner. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isArchived ? 'bg-emerald-500/10' : 'bg-red-500/10'
              }`}
            >
              {isArchived ? (
                <ArchiveRestore className="h-6 w-6 text-emerald-600" />
              ) : (
                <Archive className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">
                {actionText} Practitioner
              </DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                {isArchived
                  ? 'Restore practitioner access'
                  : 'Remove practitioner access'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-slate-700">
            Are you sure you want to {action}{' '}
            <span className="font-semibold">{practitioner.full_name}</span>?
          </p>
          <p className="text-sm text-slate-500 mt-2">{actionDescription}</p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={
              isArchived
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-red-600 hover:bg-red-700'
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isArchived ? 'Restoring...' : 'Archiving...'}
              </>
            ) : (
              <>
                {isArchived ? (
                  <ArchiveRestore className="h-4 w-4" />
                ) : (
                  <Archive className="h-4 w-4" />
                )}
                {actionText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArchivePractitionerDialog;
